/**
 * Guide player — spotlight walkthroughs for the guides defined in
 * @proclaw/shared (ui-guides.ts). Serves two entry points:
 *
 *  1. First login: a welcome modal offering the "platform-basics" guide,
 *     remembered per user in localStorage.
 *  2. On demand: `proclaw:start-guide` (detail.guideId) — dispatched by
 *     Scout's "Show me" buttons and its "Take the platform tour" chip
 *     (which uses the legacy `proclaw:start-tour` alias).
 *
 * Steps anchor to `data-tour` attributes and get a box-shadow spotlight +
 * positioned card; steps may declare a route (":tenantId" substituted) and
 * the player navigates there first. Anchorless steps render as a centered
 * card. Missing anchors skip gracefully.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { getUiGuide, type UiGuideStep } from "@proclaw/shared";
import { useAuth } from "@/context/auth-context";
import { useTenant } from "@/context/tenant-context";

const storageKey = (userId: string) => `proclaw.tour.v1.${userId}`;

/** Wait for an element to exist (anchors render after route changes). */
function waitForElement(selector: string, timeoutMs = 2500): Promise<Element | null> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - started > timeoutMs) {
        resolve(null);
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
  });
}

interface SpotRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function PlatformTour() {
  const { user, isAuthenticated } = useAuth();
  const { tenantId, isLoading: tenantLoading, onboardingComplete } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [steps, setSteps] = useState<UiGuideStep[] | null>(null);
  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const [rect, setRect] = useState<SpotRect | null>(null);
  const offeredRef = useRef(false);
  // The active guide's steps, readable from stale closures (event handlers).
  const stepsRef = useRef<UiGuideStep[] | null>(null);

  const markDone = useCallback(() => {
    if (user?.id) {
      try {
        localStorage.setItem(storageKey(user.id), new Date().toISOString());
      } catch {
        /* storage unavailable — the tour just re-offers next visit */
      }
    }
  }, [user?.id]);

  const endGuide = useCallback(
    (remember: boolean) => {
      setSteps(null);
      stepsRef.current = null;
      setStepIndex(null);
      setRect(null);
      if (remember) markDone();
    },
    [markDone],
  );

  const positionStep = useCallback(
    async (guideSteps: UiGuideStep[], index: number): Promise<boolean> => {
      const step = guideSteps[index];
      if (!step) return false;
      if (step.route) {
        const target = step.route.replace(":tenantId", tenantId);
        if (!window.location.pathname.startsWith(target)) {
          navigate(target);
        }
      }
      if (!step.anchor) {
        setRect(null); // centered card
        return true;
      }
      const el = await waitForElement(step.anchor);
      if (!el) return false;
      (el as HTMLElement).scrollIntoView({ block: "nearest" });
      const r = el.getBoundingClientRect();
      const pad = 6;
      setRect({
        top: r.top - pad,
        left: r.left - pad,
        width: r.width + pad * 2,
        height: r.height + pad * 2,
      });
      return true;
    },
    [navigate, tenantId],
  );

  const goToStep = useCallback(
    async (guideSteps: UiGuideStep[], index: number, direction: 1 | -1 = 1) => {
      for (let i = index; i >= 0 && i < guideSteps.length; i += direction) {
        // eslint-disable-next-line no-await-in-loop
        if (await positionStep(guideSteps, i)) {
          setStepIndex(i);
          return;
        }
      }
      endGuide(true);
    },
    [positionStep, endGuide],
  );

  const startGuide = useCallback(
    (guideId: string) => {
      const guide = getUiGuide(guideId);
      if (!guide) return;
      setWelcomeOpen(false);
      setSteps(guide.steps);
      stepsRef.current = guide.steps;
      void goToStep(guide.steps, 0);
    },
    [goToStep],
  );

  // First-login offer: once per user (per browser), only after the tenant
  // itself finished company onboarding so the two wizards never stack.
  useEffect(() => {
    if (offeredRef.current || tenantLoading || !isAuthenticated || !user?.id) return;
    if (!onboardingComplete) return;
    if (location.pathname.includes("/onboarding")) return;
    let seen = null;
    try {
      seen = localStorage.getItem(storageKey(user.id));
    } catch {
      seen = "unavailable";
    }
    if (!seen) {
      offeredRef.current = true;
      setWelcomeOpen(true);
    }
  }, [tenantLoading, isAuthenticated, user?.id, onboardingComplete, location.pathname]);

  // On-demand guides (Scout's "Show me" buttons) + legacy tour alias.
  useEffect(() => {
    const onStartGuide = (e: Event) => {
      const guideId = (e as CustomEvent<{ guideId?: string }>).detail?.guideId;
      if (guideId) startGuide(guideId);
    };
    const onStartTour = () => startGuide("platform-basics");
    window.addEventListener("proclaw:start-guide", onStartGuide);
    window.addEventListener("proclaw:start-tour", onStartTour);
    return () => {
      window.removeEventListener("proclaw:start-guide", onStartGuide);
      window.removeEventListener("proclaw:start-tour", onStartTour);
    };
  }, [startGuide]);

  // Keep the spotlight glued to its anchor on resize.
  useEffect(() => {
    if (stepIndex === null) return;
    const onResize = () => {
      if (stepsRef.current) void positionStep(stepsRef.current, stepIndex);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [stepIndex, positionStep]);

  // ── Welcome modal ────────────────────────────────────────────────────
  if (welcomeOpen) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
        <div className="mx-4 w-full max-w-sm rounded-2xl border border-border bg-surface p-6 shadow-2xl">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles size={20} className="text-accent" />
            <h2 className="m-0 text-[16px] font-semibold text-text-primary">Welcome to ProClaw</h2>
          </div>
          <p className="m-0 mb-5 text-[13.5px] leading-relaxed text-text-secondary">
            This is your AI agent workspace. Take a 60-second tour of the essentials — talking to
            agents, channels and chats, sharing files, and where to get help?
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setWelcomeOpen(false);
                markDone();
              }}
              className="rounded-lg px-3 py-2 text-[13px] font-medium text-text-muted hover:bg-surface-hover hover:text-text-primary"
            >
              Skip for now
            </button>
            <button
              onClick={() => startGuide("platform-basics")}
              className="rounded-lg bg-accent px-4 py-2 text-[13px] font-medium text-white hover:bg-accent-hover"
            >
              Take the tour
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Guide steps ──────────────────────────────────────────────────────
  if (stepIndex === null || !steps) return null;
  const step = steps[stepIndex]!;
  const isLast = stepIndex === steps.length - 1;

  // Card position: anchored steps sit beside the spotlight; anchorless
  // steps render centered.
  const centered = !step.anchor || !rect;
  const tooltipBelow = rect ? rect.top + rect.height + 190 < window.innerHeight : true;
  // Cap the card to the space actually available and let it scroll, so a tall
  // step near a screen edge can't push its "Next" button off-screen.
  const tooltipStyle: React.CSSProperties = centered
    ? {
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: 360,
        maxHeight: "calc(100vh - 24px)",
        overflowY: "auto",
      }
    : {
        position: "fixed",
        left: Math.min(Math.max(rect!.left, 12), window.innerWidth - 342),
        ...(tooltipBelow
          ? {
              top: rect!.top + rect!.height + 10,
              maxHeight: window.innerHeight - (rect!.top + rect!.height + 10) - 12,
            }
          : {
              bottom: window.innerHeight - rect!.top + 10,
              maxHeight: rect!.top - 22,
            }),
        width: 330,
        overflowY: "auto",
      };

  return (
    <>
      {/* Click shield — keeps the user in the guide; X always exits */}
      <div
        className={`fixed inset-0 z-[68] ${centered ? "bg-black/45" : ""}`}
        onClick={() => void 0}
      />
      {/* Spotlight cutout */}
      {!centered && (
        <div
          className="pointer-events-none fixed z-[69] rounded-lg border-2 border-accent transition-all duration-200"
          style={{
            top: rect!.top,
            left: rect!.left,
            width: rect!.width,
            height: rect!.height,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
          }}
        />
      )}
      {/* Step card */}
      <div
        style={tooltipStyle}
        className="z-[70] rounded-xl border border-border bg-surface p-4 shadow-2xl"
      >
        <div className="mb-1 flex items-start justify-between gap-2">
          <p className="m-0 text-[14px] font-semibold text-text-primary">{step.title}</p>
          <button
            onClick={() => endGuide(true)}
            aria-label="End tour"
            className="rounded p-0.5 text-text-muted hover:text-text-primary"
          >
            <X size={14} />
          </button>
        </div>
        <p className="m-0 mb-3 text-[12.5px] leading-relaxed text-text-secondary">{step.body}</p>
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${i === stepIndex ? "bg-accent" : "bg-border"}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {stepIndex > 0 && (
              <button
                onClick={() => void goToStep(steps, stepIndex - 1, -1)}
                className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-text-muted hover:bg-surface-hover hover:text-text-primary"
              >
                Back
              </button>
            )}
            <button
              onClick={() => (isLast ? endGuide(true) : void goToStep(steps, stepIndex + 1))}
              className="rounded-lg bg-accent px-3.5 py-1.5 text-[12.5px] font-medium text-white hover:bg-accent-hover"
            >
              {isLast ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
